import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { GoalsReviewWizard } from './components/GoalsReviewWizard';
import type { GoalsReviewData } from '../shared/types/wizard.types';
import { useSaveGoalsReview } from '../shared/hooks/useOneOnOneWizard';
import { useUserAssignedGoals } from '@/shared/hooks/useGoals';
import type {
  PopulatedInteraction,
  Interaction,
  TaskWithPopulatedAssignees,
} from '@momentum/types';
import {
  useCompleteStage,
  useGetOneOnOneById,
  useUpdateOneOnOne,
} from '@/shared/hooks/useOneOnOne';
import { OneOnOneStage, OneOnOneStatus } from '@momentum/types';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';

interface GoalsReviewStageProps {
  sessionId: string;
  employeeId: string;
  onComplete: () => void;
}

export const GoalsReviewStage = ({
  sessionId,
  employeeId,
  onComplete,
}: GoalsReviewStageProps) => {
  const { t } = useTranslation('oneOnOne');
  const currentEmployee = useEmployeeContext();
  const saveGoalsReviewMutation = useSaveGoalsReview();
  const completeStage = useCompleteStage();
  const updateOneOnOneMutation = useUpdateOneOnOne();

  const { data: oneOnOneData } = useGetOneOnOneById(sessionId);

  // Check if current user is the employee
  const isEmployee =
    oneOnOneData?.employeeId.toString() === currentEmployee._id.toString();

  const {
    data: goalsWithInteractions,
    isLoading,
    error,
  } = useUserAssignedGoals(
    employeeId,
    oneOnOneData?.meetingType,
    oneOnOneData?.managerId
  );

  // Track newly created interactions
  const [newInteractions, setNewInteractions] = useState<
    Array<{ goalId: string; interaction: Interaction }>
  >([]);

  // Callback to handle newly created interactions
  const handleInteractionCreated = useCallback(
    (interaction: Interaction, goalId: string) => {
      setNewInteractions((prev) => [...prev, { goalId, interaction }]);
    },
    []
  );

  // Transform API response to separate goals, interactions, and tasks by goal
  const { goals, interactionsByGoal, tasksByGoal } = useMemo(() => {
    if (!goalsWithInteractions) {
      return { goals: [], interactionsByGoal: {}, tasksByGoal: {} };
    }

    const goalsData = goalsWithInteractions.map((item) => {
      const { interactions, tasks, ...goal } = item;
      return goal;
    });

    const interactionsMap = goalsWithInteractions.reduce((acc, item) => {
      acc[item._id] = item.interactions as PopulatedInteraction[];
      return acc;
    }, {} as Record<string, PopulatedInteraction[]>);

    const tasksMap = goalsWithInteractions.reduce((acc, item) => {
      acc[item._id] = (item.tasks || []) as TaskWithPopulatedAssignees[];
      return acc;
    }, {} as Record<string, TaskWithPopulatedAssignees[]>);

    return {
      goals: goalsData,
      interactionsByGoal: interactionsMap,
      tasksByGoal: tasksMap,
    };
  }, [goalsWithInteractions]);

  // Merge newly created interactions with fetched data
  const mergedInteractionsByGoal = useMemo(() => {
    const merged = { ...interactionsByGoal };

    newInteractions.forEach(({ goalId, interaction }) => {
      // Cast to PopulatedInteraction for display - the UI will show what it can
      // When user navigates away and comes back, fresh populated data will be fetched
      merged[goalId] = [
        ...(merged[goalId] || []),
        interaction as unknown as PopulatedInteraction,
      ];
    });

    return merged;
  }, [interactionsByGoal, newInteractions]);

  const handleComplete = useCallback(async () => {
    if (saveGoalsReviewMutation.isPending) return;

    try {
      const goalsReviewData: GoalsReviewData = {
        reviewedGoals: goals.map((goal) => goal._id),
        totalGoals: goals.length,
        completedAt: new Date().toISOString(),
      };

      await saveGoalsReviewMutation.mutateAsync(goalsReviewData);

      // Complete the stage (idempotent - if already completed, skip)
      const isStageAlreadyCompleted = oneOnOneData?.completedStages?.includes(
        OneOnOneStage.GOALS_REVIEW
      );

      if (!isStageAlreadyCompleted) {
        await completeStage.mutateAsync({
          oneOnOneId: sessionId,
          stage: OneOnOneStage.GOALS_REVIEW,
        });
      }

      if (isEmployee) {
        await updateOneOnOneMutation.mutateAsync({
          id: sessionId,
          data: {
            status: OneOnOneStatus.READY_FOR_SUBMISSION,
          },
        });

        toast.success(t('goalsReviewSaved'), {
          description: t('sentToManager'),
        });
      } else {
        const successfullySavedReview = t('successfullySavedReview') as (
          count: number
        ) => string;
        toast.success(t('goalsReviewSaved'), {
          description: successfullySavedReview(goalsReviewData.totalGoals),
        });
      }

      onComplete();
    } catch (error) {
      console.error('Failed to complete goals review:', error);
      toast.error(t('failedToSave'), {
        description:
          error instanceof Error ? error.message : t('couldNotSaveReview'),
      });
    }
  }, [
    goals,
    saveGoalsReviewMutation,
    completeStage,
    updateOneOnOneMutation,
    sessionId,
    isEmployee,
    oneOnOneData?.completedStages,
    onComplete,
    t,
  ]);

  if (isLoading) {
    return <div>Loading goals...</div>;
  }

  if (error) {
    const errorMessage = `Error loading goals: ${error.message}`;
    return <div>{errorMessage}</div>;
  }

  return (
    <GoalsReviewWizard
      goals={goals}
      interactionsByGoal={mergedInteractionsByGoal}
      tasksByGoal={tasksByGoal}
      employeeId={employeeId}
      onComplete={handleComplete}
      onInteractionCreated={handleInteractionCreated}
      lastOneOnOneDate={oneOnOneData?.lastOneOnOneDate ?? null}
    />
  );
};
